const ERROR_CODES = new Set([
  'postal_code_mismatch', 'pricing_or_shipping_pending', 'shipping_quote_expired',
  'PRICE_CHANGED', 'CART_OPTIONS_CHANGED', 'QUOTE_NOT_FOUND', 'QUOTE_PRICE_INCOMPLETE', 'QUOTE_VAT_MISMATCH',
  'QUOTE_DISCOUNT_NOT_ALLOWED', 'SUPPLIER_PRODUCT_UNAVAILABLE', 'BOOG_MATERIAL_FILE_MISMATCH',
  'CHECKOUT_INTENT_REVIEW_REQUIRED', 'DISCOUNT_ECONOMICS_UNKNOWN', 'DISCOUNT_CONTRIBUTION_UNSAFE',
  'ORDER_CONTRIBUTION_UNSAFE', 'invalid_order_response', 'EDITOR_ACCESS_REQUIRED',
  'UPLOAD_TOO_LARGE', 'UPLOAD_FAILED', 'UPLOAD_TIMEOUT', 'DESIGN_SAVE_FAILED',
]);
const PLATFORMS = new Set(['windows', 'macos', 'ios', 'android', 'linux', 'unknown']);
export type SafeErrorContext = { code?: string; durationMs?: number; release?: string; browserPlatform?: string };
export function safeErrorContext(input: unknown): SafeErrorContext {
  const value = input && typeof input === 'object' && !Array.isArray(input) ? input as Record<string, unknown> : {};
  return {
    ...(typeof value.code === 'string' && (ERROR_CODES.has(value.code) || /^http_[45]\d{2}$/.test(value.code)) ? { code: value.code } : {}),
    ...(typeof value.durationMs === 'number' && Number.isSafeInteger(value.durationMs) && value.durationMs >= 0 && value.durationMs <= 3600000 ? { durationMs: value.durationMs } : {}),
    ...(typeof value.release === 'string' && /^[a-f0-9]{7,40}$/i.test(value.release) ? { release: value.release.toLowerCase() } : {}),
    ...(typeof value.browserPlatform === 'string' && PLATFORMS.has(value.browserPlatform) ? { browserPlatform: value.browserPlatform } : {}),
  };
}
export function runtimeErrorContext(): SafeErrorContext {
  let browserPlatform: string | undefined;
  try {
    if (typeof navigator !== 'undefined') {
      const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || '';
      browserPlatform = /android/i.test(platform) ? 'android' : /iphone|ipad|ipod|ios/i.test(platform) ? 'ios'
        : /win/i.test(platform) ? 'windows' : /mac/i.test(platform) ? 'macos' : /linux/i.test(platform) ? 'linux' : 'unknown';
    }
  } catch { /* An unavailable platform is not grounds to drop a report. */ }
  return safeErrorContext({ release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA || process.env.VERCEL_GIT_COMMIT_SHA,
    browserPlatform });
}
/** Keep operational numbers/identifiers, never caller-supplied files or customer fields. */
export function safeOrderPathContext(context: Record<string, unknown>) {
  const code = safeErrorContext(context).code || safeErrorContext({ code: context.reason }).code;
  const extra: Record<string, string | number | boolean> = { ...safeErrorContext({ ...context, code }), ...runtimeErrorContext() };
  for (const key of ['bytes', 'loaded', 'percentage', 'pixelWidth', 'pixelHeight', 'dpi', 'stalledForMs']) {
    const value = context[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER) extra[key] = value;
  }
  if (typeof context.status === 'number' && Number.isInteger(context.status) && context.status >= 100 && context.status <= 599) extra.status = context.status;
  for (const key of ['product', 'providerType', 'stage', 'partner', 'path', 'source', 'orderId', 'orderNumber']) {
    const value = context[key];
    if (typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value)) extra[key] = value;
  }
  for (const key of ['multipart', 'abortedByWatchdog']) if (typeof context[key] === 'boolean') extra[key] = context[key] as boolean;
  return extra;
}
